/**
 * P2.3b: Full i18n coverage
 * CLEANUP SPRINT: Migrated from ClinicDataContext to backend API via useClinicLeads
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LEAD_STATUSES } from '../../../context/ClinicDataContext';
import { useClinicLeads } from '../../../hooks/useClinicLeads';
import { useSalesReps } from '../../../hooks/useSalesReps';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  Users, UserPlus, Zap, RefreshCw, ChevronRight,
  ArrowRight, CheckCircle, Settings
} from 'lucide-react';
import { toast } from '../../../hooks/use-toast';

const LeadDistribution = ({ clinicId, onRefresh }) => {
  const { t } = useTranslation();

  // API-driven lead operations
  const {
    leads: apiLeads,
    fetchLeads,
    assignLead: apiAssignLead,
  } = useClinicLeads(clinicId);

  // Phase 2A: API-driven sales reps
  const { salesReps: apiSalesReps, fetchSalesReps } = useSalesReps(clinicId);

  // Helper to get patient info from lead (backend snake_case fields)
  const getPatientForLead = (lead) => {
    return {
      name: lead?.patient_name || lead?.patientName || 'Unknown',
      email: lead?.patient_email || lead?.patientEmail || '',
      phone: lead?.patient_phone || lead?.patientPhone || '',
      country: lead?.country_of_origin || lead?.patientCountry || 'Unknown',
    };
  };

  const [leads, setLeads] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [draggedLead, setDraggedLead] = useState(null);
  const [distributionMode, setDistributionMode] = useState('round-robin');

  const loadData = useCallback(() => {
    fetchLeads();
    fetchSalesReps();
  }, [clinicId, fetchLeads, fetchSalesReps]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Sync API data into local state
  useEffect(() => {
    if (apiSalesReps?.length >= 0) setSalesReps(apiSalesReps);
  }, [apiSalesReps]);

  // Sync API leads into local state
  useEffect(() => {
    if (apiLeads) setLeads(apiLeads);
  }, [apiLeads]);

  const unassignedLeads = leads.filter(l =>
    !l.assigned_agent_id && l.status === 'NEW'
  );

  const handleAssign = async (leadId, repId) => {
    try {
      const rep = salesReps.find(r => r.id === repId);
      await apiAssignLead(leadId, repId, rep?.name);
      fetchLeads();
      toast({ title: t('provider.leads.distribution.toastAssignedTitle'), description: t('provider.leads.distribution.toastAssignedDesc') });
    } catch (err) {
      toast({ title: t('provider.leads.mgmt.toastError'), description: err.response?.data?.detail || t('provider.leads.distribution.toastAssignFailed'), variant: 'destructive' });
    }
  };

  const handleUnassign = async (leadId) => {
    try {
      await apiAssignLead(leadId, null);
      fetchLeads();
      toast({ title: t('provider.leads.distribution.toastUnassignedTitle'), description: t('provider.leads.distribution.toastUnassignedDesc') });
    } catch (err) {
      toast({ title: t('provider.leads.mgmt.toastError'), description: err.response?.data?.detail || t('provider.leads.distribution.toastUnassignFailed'), variant: 'destructive' });
    }
  };

  const handleAutoDistribute = async () => {
    if (unassignedLeads.length === 0 || salesReps.length === 0) {
      toast({ title: t('provider.leads.distribution.toastNoLeadsTitle'), description: t('provider.leads.distribution.toastNoLeadsDesc'), variant: 'destructive' });
      return;
    }
    let count = 0;
    for (let i = 0; i < unassignedLeads.length; i++) {
      const rep = salesReps[i % salesReps.length];
      try {
        await apiAssignLead(unassignedLeads[i].id, rep.id, rep.name);
        count++;
      } catch { /* skip failed */ }
    }
    fetchLeads();
    toast({ title: t('provider.leads.distribution.toastAutoDoneTitle'), description: t('provider.leads.distribution.toastAutoDoneDesc', { count }) });
  };

  const handleDragStart = (e, lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, repId) => {
    e.preventDefault();
    if (draggedLead) {
      handleAssign(draggedLead.id, repId);
      setDraggedLead(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('provider.leads.distribution.title')}</h2>
          <p className="text-gray-500">{t('provider.leads.distribution.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={distributionMode}
            onChange={(e) => setDistributionMode(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="round-robin">{t('provider.leads.distribution.modeRoundRobin')}</option>
            <option value="specialization">{t('provider.leads.distribution.modeSpecialization')}</option>
          </select>
          <Button onClick={handleAutoDistribute} disabled={unassignedLeads.length === 0}>
            <Zap className="h-4 w-4 mr-2" />
            {t('provider.leads.distribution.autoDistributeBtn', { count: unassignedLeads.length })}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Unassigned Leads Pool */}
        <Card className="lg:col-span-1">
          <CardHeader className="bg-slate-50">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-blue-500" />
              {t('provider.leads.distribution.unassignedTitle')}
            </CardTitle>
            <CardDescription>{t('provider.leads.distribution.unassignedWaiting', { count: unassignedLeads.length })}</CardDescription>
          </CardHeader>
          <CardContent className="p-4 max-h-[600px] overflow-y-auto">
            {unassignedLeads.length > 0 ? (
              <div className="space-y-3">
                {unassignedLeads.map(lead => {
                  const patient = getPatientForLead(lead);
                  return (
                    <div
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      className={`p-4 border rounded-lg cursor-move hover:shadow-md transition-shadow bg-white ${
                        selectedLead?.id === lead.id ? 'ring-2 ring-blue-500' : ''
                      }`}
                      onClick={() => setSelectedLead(lead)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium">{patient.name}</p>
                        <Badge variant="outline" className="text-xs">{patient.country}</Badge>
                      </div>
                      <Badge className="mb-2">{lead.treatment_display_name || lead.treatment_interest || lead.treatment}</Badge>
                      <div className="text-xs text-gray-500 flex justify-between">
                        <span>{lead.budget_range || lead.intakeSummary?.budget}</span>
                        <span>{lead.source_type || lead.packageType}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                <p>{t('provider.leads.distribution.allAssigned')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sales Reps */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="text-lg font-semibold">{t('provider.leads.distribution.salesRepsHeading')}</h3>
          <p className="text-sm text-gray-500">{t('provider.leads.distribution.salesRepsHelp')}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {salesReps.map(rep => {
              const repLeads = leads.filter(l => l.assigned_agent_id === rep.rep_id);
              const closedWon = repLeads.filter(l => l.status === 'CLOSED_WON').length;
              const stats = {
                totalLeads: repLeads.length,
                activeLeads: repLeads.filter(l => !['CLOSED_WON', 'LOST', 'CLOSED_LOST'].includes(l.status)).length,
                offersSent: repLeads.filter(l => l.status === 'OFFER_SENT').length,
                conversionRate: repLeads.length > 0 ? Math.round((closedWon / repLeads.length) * 100) : 0,
              };
              
              return (
                <Card
                  key={rep.id}
                  className="hover:shadow-lg transition-shadow"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, rep.id)}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <img src={rep.avatar} alt="" className="w-12 h-12 rounded-full" />
                      <div className="flex-1">
                        <CardTitle className="text-base">{rep.name}</CardTitle>
                        <CardDescription className="text-xs">
                          {rep.specializations?.join(', ')}
                        </CardDescription>
                      </div>
                      <Badge variant="secondary">{t('provider.leads.distribution.statActive', { count: stats.activeLeads })}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mb-4 text-center">
                      <div className="bg-blue-50 p-2 rounded">
                        <p className="text-lg font-bold text-blue-600">{stats.totalLeads}</p>
                        <p className="text-xs text-gray-500">{t('provider.leads.distribution.statTotal')}</p>
                      </div>
                      <div className="bg-purple-50 p-2 rounded">
                        <p className="text-lg font-bold text-purple-600">{stats.offersSent}</p>
                        <p className="text-xs text-gray-500">{t('provider.leads.distribution.statOffers')}</p>
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <p className="text-lg font-bold text-green-600">{stats.conversionRate}%</p>
                        <p className="text-xs text-gray-500">{t('provider.leads.distribution.statConv')}</p>
                      </div>
                    </div>

                    {/* Assigned Leads */}
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {repLeads.slice(0, 5).map(lead => {
                        const patient = getPatientForLead(lead);
                        return (
                          <div key={lead.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{patient.name}</p>
                              <p className="text-xs text-gray-500 truncate">{lead.treatment_display_name || lead.treatment_interest || lead.treatment}</p>
                            </div>
                            <Badge className={`ml-2 text-xs ${LEAD_STATUSES[lead.status]?.color}`}>
                              {t(`provider.leads.statuses.${lead.status}`, LEAD_STATUSES[lead.status]?.label || lead.status)}
                            </Badge>
                          </div>
                        );
                      })}
                      {repLeads.length > 5 && (
                        <p className="text-xs text-center text-gray-500">{t('provider.leads.distribution.moreCount', { count: repLeads.length - 5 })}</p>
                      )}
                      {repLeads.length === 0 && (
                        <p className="text-sm text-gray-400 text-center py-4">{t('provider.leads.distribution.noLeadsAssigned')}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDistribution;